import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import Helmet from 'react-helmet';
import { Button, CircularProgress, Box, Paper } from '@material-ui/core';
import { useHistory, useParams } from 'react-router-dom';
import { Typography } from '@mui/material';

import { showErrorAction, showMessageAction } from 'containers/Notification/actions';
import { useHandleNonprofitAddressChangeMutation } from 'generated/graphql';
import { renderAddress } from 'utils/renderAddress';

import { RejectAddressChangeModal } from './RejectAddressChangeModal';

const AddressChangeRequestPage = () => {
  const { data } = useParams<{ data: string }>();
  const history = useHistory();
  const dispatch = useDispatch();
  const [handleNonprofitAddressChangeMutation, { loading: submitting }] =
    useHandleNonprofitAddressChangeMutation({
      onError: () => {
        dispatch(showErrorAction('Failed to process a request'));
      },
    });
  const { newAddress, ticketId, nonprofitId, userId } = JSON.parse(atob(data));

  const [rejectionModalOpen, setRejectionModalOpen] = useState(false);

  const onConfirm = async () => {
    const response = await handleNonprofitAddressChangeMutation({
      variables: { ticketId, nonprofitId, userId, approve: true },
    });
    if ((response as { errors: unknown }).errors) {
      dispatch(showErrorAction('Failed to confirm the address change, please try again'));
    } else {
      dispatch(
        showMessageAction(
          'The address change has been confirmed, the Zendesk ticket should have been updated and closed'
        )
      );
      history.replace('/');
    }
  };

  const onReject = async ({ rejectionReason }: { rejectionReason: string }) => {
    const response = await handleNonprofitAddressChangeMutation({
      variables: { ticketId, nonprofitId, userId, approve: false, rejectionReason },
    });
    if ((response as { errors: unknown }).errors) {
      dispatch(showErrorAction('Failed to confirm the address change, please try again'));
    } else {
      dispatch(
        showMessageAction(
          'The address change has been rejected, the Zendesk ticket should have been updated and closed'
        )
      );
      history.replace('/');
    }
  };

  return (
    <Box maxWidth={1200}>
      <Paper variant="outlined" style={{ padding: 24, paddingTop: 0, position: 'relative' }}>
        <Helmet title="Address change request" />
        <h2>
          Address change request{' '}
          <a href={`https://joindeed.zendesk.com/agent/tickets/${ticketId}`}>#{ticketId}</a>
        </h2>

        <Box>
          <Typography>Nonprofit ID: {nonprofitId}</Typography>
          <Typography>New address: {renderAddress(newAddress)}</Typography>
        </Box>

        <Box position="absolute" bottom={24} right={24}>
          {submitting ? (
            <CircularProgress size={24} />
          ) : (
            <>
              <Button
                color="primary"
                onClick={() => setRejectionModalOpen(true)}
                disabled={submitting}
                variant="outlined"
                style={{ marginRight: 8 }}
              >
                Reject
              </Button>
              <Button color="primary" onClick={onConfirm} disabled={submitting} variant="contained">
                Confirm
              </Button>
            </>
          )}
        </Box>
      </Paper>

      {rejectionModalOpen && (
        <RejectAddressChangeModal
          onClose={() => setRejectionModalOpen(false)}
          onReject={onReject}
        />
      )}
    </Box>
  );
};

export default AddressChangeRequestPage;
